import type { SysMessageItem } from '@/types/mqtt'

const STORAGE_PREFIX = 'sysMessages'
const MAX_MESSAGES = 200

function getStorageKey(uid: number): string {
  return `${STORAGE_PREFIX}_${uid}`
}

function hasStorage(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined'
}

export const SysMessageStorage = {
  getMessages(uid: number): SysMessageItem[] {
    if (!hasStorage() || !uid) return []
    try {
      const raw = localStorage.getItem(getStorageKey(uid))
      if (!raw) return []
      const list = JSON.parse(raw) as SysMessageItem[]
      return Array.isArray(list) ? list : []
    } catch {
      return []
    }
  },

  saveMessages(uid: number, messages: SysMessageItem[]): void {
    if (!hasStorage() || !uid) return
    const sorted = [...messages]
      .sort((a, b) => (b.receivedAt ?? b.time ?? 0) - (a.receivedAt ?? a.time ?? 0))
      .slice(0, MAX_MESSAGES)
    localStorage.setItem(getStorageKey(uid), JSON.stringify(sorted))
  },

  appendMessage(uid: number, message: SysMessageItem): SysMessageItem[] {
    const messages = this.getMessages(uid)
    if (messages.some(item => item.id === message.id)) {
      return messages
    }

    const next = [message, ...messages].slice(0, MAX_MESSAGES)
    this.saveMessages(uid, next)
    return next
  },

  markAsRead(uid: number, messageId: string): SysMessageItem[] {
    const messages = this.getMessages(uid)
    const next = messages.map(item =>
      item.id === messageId ? { ...item, read: true } : item
    )
    this.saveMessages(uid, next)
    return next
  },

  markAllAsRead(uid: number): SysMessageItem[] {
    const messages = this.getMessages(uid).map(item => ({ ...item, read: true }))
    this.saveMessages(uid, messages)
    return messages
  },

  removeMessage(uid: number, messageId: string): SysMessageItem[] {
    const next = this.getMessages(uid).filter(item => item.id !== messageId)
    this.saveMessages(uid, next)
    return next
  },

  clearMessages(uid: number): void {
    if (!hasStorage() || !uid) return
    localStorage.removeItem(getStorageKey(uid))
  }
}

export default SysMessageStorage
